import React, { useEffect, useRef } from 'react';
import { Animated, Modal, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Shadow, Typography } from '../../theme';
import { useI18n } from '../../i18n';

const REACTION_EMOJIS = ['❤️', '😂', '🔥', '👍', '😮', '👏'];

interface Props {
  visible: boolean;
  currentEmoji?: string;
  onSelect: (emoji: string) => void;
  onClose: () => void;
  /** Shows a "Reply" pill below the emoji row when provided. */
  onReply?: () => void;
}

/** Long-press message-action popup — reactions (+ optional reply) in one glass card, pop-in/out animation. */
const ReactionBar: React.FC<Props> = ({ visible, currentEmoji, onSelect, onClose, onReply }) => {
  const { t } = useI18n();
  const scale = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.spring(scale, {
      toValue: visible ? 1 : 0,
      useNativeDriver: true,
      speed: 22,
      bounciness: visible ? 10 : 0,
    }).start();
  }, [scale, visible]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.overlay} onPress={onClose}>
        <View>
          <Animated.View style={[styles.card, { transform: [{ scale }], opacity: scale }]}>
            <LinearGradient
              pointerEvents="none"
              colors={['rgba(255,255,255,0.12)', 'rgba(255,255,255,0.03)']}
              start={{ x: 0.1, y: 0 }}
              end={{ x: 0.9, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
            {REACTION_EMOJIS.map(emoji => (
              <TouchableOpacity
                key={emoji}
                style={[styles.emojiBtn, currentEmoji === emoji && styles.emojiBtnActive]}
                activeOpacity={0.7}
                onPress={() => { onSelect(emoji); onClose(); }}
              >
                <Text style={styles.emoji}>{emoji}</Text>
              </TouchableOpacity>
            ))}
          </Animated.View>
          {onReply ? (
            <Animated.View style={[styles.replyPill, { transform: [{ scale }], opacity: scale }]}>
              <TouchableOpacity style={styles.replyButton} activeOpacity={0.8} onPress={() => { onReply(); onClose(); }}>
                <Icon name="corner-up-left" size={14} color={Colors.textPrimary} />
                <Text style={styles.replyText}>{t('chat.reply')}</Text>
              </TouchableOpacity>
            </Animated.View>
          ) : null}
        </View>
      </Pressable>
    </Modal>
  );
};

export default ReactionBar;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    flexDirection: 'row',
    gap: 4,
    padding: 10,
    borderRadius: Radius.xxl,
    overflow: 'hidden',
    backgroundColor: 'rgba(14,21,35,0.92)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    ...Shadow.strong,
  },
  emojiBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emojiBtnActive: {
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  emoji: {
    fontSize: 24,
  },
  replyPill: {
    marginTop: 8,
    alignItems: 'center',
  },
  replyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(14,21,35,0.92)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    ...Shadow.card,
  },
  replyText: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
});
